import { useState } from 'react';

import { contactsApi } from '@/api/queries/contacts';
import { Button, useToast } from '@/components/ui';

/**
 * Editor for the contact's single general note — shared between the contacts
 * list quick panel and the detail page aside. Saves through the dedicated
 * PUT /contacts/{id}/general-note endpoint (field-scoped, no etag dance), so
 * it works independently of the detail form's etag-guarded full save.
 */
export function GeneralNoteEditor({
  contactId,
  initialText,
  rows = 5,
}: {
  contactId: string;
  initialText: string | undefined;
  rows?: number;
}) {
  const toast = useToast();
  const setNote = contactsApi.useSetGeneralNote();
  const saved = initialText ?? '';
  const [text, setText] = useState(saved);
  // Reseed when another editor (panel vs detail aside) saved a new value.
  const [prevSaved, setPrevSaved] = useState(saved);
  if (prevSaved !== saved) {
    setPrevSaved(saved);
    setText(saved);
  }
  const dirty = text.trim() !== saved.trim();

  const save = async () => {
    try {
      await setNote.mutateAsync({ id: contactId, text: text.trim() || null });
      toast.show({ title: 'Note saved', severity: 'success' });
    } catch (e) {
      toast.show({
        severity: 'error',
        title: 'Failed to save note',
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <textarea
        className="ds-input"
        rows={rows}
        placeholder="Add a general note…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{ width: '100%', resize: 'vertical' }}
      />
      {dirty && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={setNote.isPending}
            loading={setNote.isPending}
            onClick={() => void save()}
          >
            Save note
          </Button>
        </div>
      )}
    </div>
  );
}
